import React, { useState } from "react";
import api, { apiErr } from "../../lib/api";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MessageSquarePlus, Star } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "confusing", l: "Something is confusing" },
  { v: "calculation", l: "Calculation appears incorrect" },
  { v: "detection", l: "Commitment was detected incorrectly" },
  { v: "feature", l: "Feature request" },
  { v: "bug", l: "Something is not working" },
  { v: "general", l: "General feedback" },
];

export default function FeedbackWidget({ open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = onOpenChange || setOpenState;
  const [type, setType] = useState("general");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { pathname } = useLocation();

  const submit = async () => {
    if (!message.trim()) { toast.error("Please add a short note."); return; }
    setSaving(true);
    try {
      await api.post("/feedback", {
        type, page: pathname, rating: rating || null, message,
        device_type: /Mobi/.test(navigator.userAgent) ? "mobile" : "desktop",
        browser: navigator.userAgent.slice(0, 120),
      });
      toast.success("Thank you — your feedback reached the team.");
      setOpen(false); setMessage(""); setRating(0); setType("general");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-fab" aria-label="Give feedback"
        className="fixed z-40 bottom-24 right-4 md:bottom-6 md:right-6 h-11 w-11 md:h-12 md:w-auto md:pl-4 md:pr-5 rounded-full bg-navy text-white shadow-card-lg flex items-center justify-center md:gap-2 hover:bg-navy-light transition-colors">
        <MessageSquarePlus size={18} /><span className="hidden md:inline text-sm font-medium">Feedback</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-ink">Share feedback</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-ink text-sm">What's this about?</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="feedback-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-ink text-sm">How would you rate this?</Label>
              <div className="flex gap-1.5 mt-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => setRating(s)} data-testid={`feedback-star-${s}`}>
                    <Star size={26} className={s <= rating ? "fill-mint text-mint" : "text-border"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-ink text-sm">Your feedback</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1 rounded-xl" data-testid="feedback-message" />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-navy text-white hover:bg-navy-light h-11" data-testid="feedback-submit">
              {saving ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
